
import React, { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Seo from './Seo';
import { useIsMobile } from '@/hooks/use-mobile';
import BottomNavigation from './mobile/BottomNavigation';

const Layout = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const isHomePage = location.pathname === '/';
  
  // Don't render header/bottom nav on mobile home page since it has its own
  const hideMobileNav = isMobile && isHomePage;

  // Store route transitions for prefetching
  const prevRouteRef = React.useRef<string | null>(null);
  
  // Set default SEO values based on the current route
  const getDefaultSeoProps = () => {
    const path = location.pathname;
    
    if (path === '/') {
      return {
        title: 'Content Feed',
        description: 'Browse through analyzed populist content and discover insights about rhetoric patterns.'
      };
    }
    // Let individual pages set their own SEO props
    return {};
  };

  // Prefetch linked pages when they come into view
  useEffect(() => {
    // Find links in the current page
    const linkPrefetcher = () => {
      const links = document.querySelectorAll('a[href^="/"]');
      links.forEach(link => {
        const href = link.getAttribute('href');
        if (href && href !== location.pathname && !link.hasAttribute('data-prefetched')) {
          // Mark as prefetched to avoid duplicate prefetching
          link.setAttribute('data-prefetched', 'true');
          
          // Create a prefetch link in the head
          const linkEl = document.createElement('link');
          linkEl.rel = 'prefetch';
          linkEl.as = 'document';
          linkEl.href = href;
          document.head.appendChild(linkEl);
        }
      });
    };

    // Run prefetch on idle callback
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(linkPrefetcher);
    } else {
      setTimeout(linkPrefetcher, 2000); // Fallback for browsers without requestIdleCallback
    }
  }, [location.pathname]);

  // Scroll to top on route change for better mobile navigation
  useEffect(() => {
    // Only auto-scroll to top when not navigating between content feed and content detail
    const isContentRoute = location.pathname.includes('/content-feed/');
    const isFromContentFeed = location.key && prevRouteRef.current === '/content-feed';
    
    if (!(isContentRoute && isFromContentFeed)) {
      window.scrollTo(0, 0);
    }
    
    // Save current path for future reference
    prevRouteRef.current = location.pathname;
    sessionStorage.setItem('lastPath', location.pathname);
  }, [location]);
  
  return (
    <div className="min-h-screen flex flex-col">
      <Seo {...getDefaultSeoProps()} />
      {!hideMobileNav && <Header />}
      <main className={`flex-1 ${isMobile && !hideMobileNav ? 'pb-20' : ''}`}>
        <Outlet />
      </main>
      {isMobile && !hideMobileNav && <BottomNavigation />}
    </div>
  );
};

export default Layout;
